<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { BookOpenCheck, Check, ChevronLeft, ChevronRight, Flower2, Headphones, ShieldCheck, Sparkles } from '@lucide/vue'
import GoalPicker from '../components/GoalPicker.vue'
import { api, post } from '../api'
import { loadSession, session, setExperience } from '../state'
import { isEnglish } from '../i18n'

const router = useRouter(), route = useRoute()
const step = ref(0), courses = ref([]), busy = ref(false), error = ref('')
const level = ref('A1'), courseId = ref(null), chapterId = ref(null), unitId = ref(null)
const goal = ref({ mode: 'standard', new_words: 15, review_limit: 40, dictation: 10 })
const prefs = ref({ sound_enabled: true, effects_level: 'gentle', story_autoplay: true })
const selectedCourse = computed(() => courses.value.find(course => course.id === courseId.value))
const chapters = computed(() => selectedCourse.value?.chapters || [])
const selectedChapter = computed(() => chapters.value.find(chapter => chapter.id === chapterId.value))
const units = computed(() => selectedChapter.value?.units || [])
const selectedUnit = computed(() => units.value.find(unit => unit.id === unitId.value))
const l = computed(() => isEnglish.value ? {
  garden: 'German vocabulary garden', introEye: 'WILLKOMMEN · WELCOME', replay: 'Review your learning path', intro: `${session.user.display_name}, plant your first word today`,
  introText: 'Recognize it, write it, then hear it. Articles, plurals, spelling, and listening are scheduled separately.', chapters: 'Textbook learning', spell: 'Spelling & dictation', memory: 'FSRS long-term memory', checkin: 'Check-in garden',
  pathEye: '01 · LEARNING PATH', path: 'Where would you like to start?', pathText: 'New words begin at your selected chapter. Words learned before still return for review.', unit: 'Choose a Unit to start; later Units follow in order:', words: 'words',
  rhythmEye: '02 · DAILY PACE', rhythm: 'Choose a learning volume you can sustain', rhythmText: 'Complete the locked core plan to check in. Accuracy only changes later review.',
  experienceEye: '03 · LEARNING EXPERIENCE', experience: 'Keep feedback comfortable', experienceText: 'These settings sync to your other devices and can be changed later in Profile.', sound: 'Answer sounds', soundText: 'Gentle sounds for correct, incorrect, and completed tasks', autoplay: 'Read stories automatically', autoplayText: 'Start German narration after opening or generating a story', animation: 'Animation intensity', animationText: 'Your device’s reduced-motion setting always takes priority', off: 'Off', gentle: 'Gentle', rich: 'Rich',
  back: 'Back', continue: 'Continue', saving: 'Saving…', enter: 'Enter my word garden',
} : {
  garden: '德语词汇花园', introEye: 'WILLKOMMEN · 欢迎', replay: '再看一次学习路线', intro: `${session.user.display_name}，从今天种下第一颗词`, introText: '先认得，再写得出，最后听得懂。系统会把冠词、复数、拼写和听音分别安排复习。', chapters: '章节式学习', spell: '拼写与听写', memory: 'FSRS 长期记忆', checkin: '打卡花园', pathEye: '01 · 学习起点', path: '从哪本书、哪一章开始？', pathText: '新词从所选章节向后安排；以前学过的词仍会正常复习。', unit: '再选一个小 Unit 开始，之后会按顺序继续：', words: '词', rhythmEye: '02 · 每日节奏', rhythm: '选一个能长期坚持的学习量', rhythmText: '完成锁定的核心任务即可打卡，正确率只影响后续复习安排。', experienceEye: '03 · 学习体验', experience: '让反馈保持舒适', experienceText: '这些设置会同步到你的其他设备，之后可在“我的”随时修改。', sound: '答题音效', soundText: '答对、答错和完成任务使用柔和提示音', autoplay: '自动朗读短文', autoplayText: '生成或打开短文后自动开始德语朗读', animation: '动画强度', animationText: '系统仍会遵循设备的“减少动态效果”设置', off: '关闭', gentle: '温和有成就感', rich: '丰富庆祝', back: '上一步', continue: '继续', saving: '正在保存…', enter: '进入我的词汇花园',
})

function chooseLevel(code) {
  level.value = code
  const course = courses.value.find(item => item.code === code)
  courseId.value = course?.id || null
  chapterId.value = course?.chapters?.[0]?.id || null
  unitId.value = course?.chapters?.[0]?.units?.[0]?.id || null
}
function chooseChapter(chapter) { chapterId.value = chapter.id; unitId.value = chapter.units?.[0]?.id || null }
function next() { if (step.value < 3) step.value += 1 }
function back() { if (step.value > 0) step.value -= 1 }
async function complete() {
  if (!courseId.value || !chapterId.value || !unitId.value) return
  busy.value = true; error.value = ''
  try {
    await post('/api/onboarding/complete', { ...goal.value, ...prefs.value, course_id: courseId.value, chapter_id: chapterId.value, unit_id: unitId.value })
    setExperience(prefs.value)
    await loadSession()
    router.replace('/')
  } catch (e) { error.value = e.message } finally { busy.value = false }
}
onMounted(async () => {
  try {
    const [courseData, goalData, pathData, experienceData] = await Promise.all([
      api('/api/courses'), api('/api/settings/study-goal'), api('/api/settings/learning-path'), api('/api/settings/experience'),
    ])
    courses.value = courseData.courses
    goal.value = { mode: goalData.goal.mode, new_words: goalData.goal.new_words, review_limit: goalData.goal.review_limit, dictation: goalData.goal.dictation }
    prefs.value = { ...experienceData.experience }
    const path = pathData.learning_path
    if (path.course_id && path.start_chapter_id) {
      courseId.value = path.course_id; chapterId.value = path.start_chapter_id; unitId.value = path.start_unit_id || courseData.courses.find(course => course.id === path.course_id)?.chapters.find(chapter => chapter.id === path.start_chapter_id)?.units?.[0]?.id || null; level.value = path.course_code
    } else chooseLevel('A1')
  } catch (e) { error.value = e.message }
})
</script>

<template>
  <main class="welcome-page">
    <!-- 环境光斑：落地页在 App 壳外渲染，需自带奶玻璃背景（fixed 定位，不占网格行） -->
    <div class="ambient-bg" aria-hidden="true"><i class="blob blob-1"></i><i class="blob blob-2"></i><i class="blob blob-3"></i></div>
    <header class="welcome-brand"><span class="brand-mark">W</span><span><b>Wortgarten</b><small>{{l.garden}}</small></span></header>
    <div class="welcome-progress"><span v-for="n in 4" :key="n" :class="{ active: n - 1 <= step }"></span></div>

    <section v-if="step === 0" class="welcome-card intro-card">
      <img class="art-matte" src="/garden-stage-3.png" :alt="l.garden" />
      <div><p class="eyebrow">{{l.introEye}}</p><h1>{{ route.query.replay === '1' ? l.replay : l.intro }}</h1><p class="welcome-lead">{{l.introText}}</p>
        <div class="welcome-features"><span><BookOpenCheck />{{l.chapters}}</span><span><Headphones />{{l.spell}}</span><span><ShieldCheck />{{l.memory}}</span><span><Flower2 />{{l.checkin}}</span></div>
      </div>
    </section>

    <section v-else-if="step === 1" class="welcome-card">
      <p class="eyebrow">{{l.pathEye}}</p><h1>{{l.path}}</h1><p class="welcome-lead">{{l.pathText}}</p>
      <div class="level-tabs"><button v-for="course in courses" :key="course.id" :class="{ active: level === course.code }" @click="chooseLevel(course.code)">{{ course.code }} · {{ course.title }}</button></div>
      <div class="chapter-choice-grid"><button v-for="chapter in chapters" :key="chapter.id" :class="{ active: chapterId === chapter.id }" @click="chooseChapter(chapter)"><span>{{ String(chapter.number).padStart(2, '0') }}</span><b>Kapitel {{ chapter.number }}</b><small>{{ chapter.title.replace(`Kapitel ${chapter.number} · `, '') }}</small><Check v-if="chapterId === chapter.id" /></button></div>
      <div v-if="units.length" class="unit-choice"><p>{{l.unit}}</p><div><button v-for="unit in units" :key="unit.id" :class="{ active: unitId === unit.id }" @click="unitId = unit.id"><b>Unit {{ unit.number }}</b><small>{{ unit.title }} · {{ unit.word_count }} {{l.words}}</small></button></div></div>
    </section>

    <section v-else-if="step === 2" class="welcome-card">
      <p class="eyebrow">{{l.rhythmEye}}</p><h1>{{l.rhythm}}</h1><p class="welcome-lead">{{l.rhythmText}}</p><GoalPicker v-model="goal" />
    </section>

    <section v-else class="welcome-card">
      <p class="eyebrow">{{l.experienceEye}}</p><h1>{{l.experience}}</h1><p class="welcome-lead">{{l.experienceText}}</p>
      <div class="preference-list">
        <label><span><b>{{l.sound}}</b><small>{{l.soundText}}</small></span><input v-model="prefs.sound_enabled" type="checkbox" /></label>
        <label><span><b>{{l.autoplay}}</b><small>{{l.autoplayText}}</small></span><input v-model="prefs.story_autoplay" type="checkbox" /></label>
        <div><span><b>{{l.animation}}</b><small>{{l.animationText}}</small></span><select v-model="prefs.effects_level"><option value="off">{{l.off}}</option><option value="gentle">{{l.gentle}}</option><option value="rich">{{l.rich}}</option></select></div>
      </div>
      <div class="welcome-summary"><Sparkles /><span><b>{{ level }} · Kapitel {{ selectedChapter?.number }} · Unit {{ selectedUnit?.number }}</b><small>{{ goal.new_words }} {{isEnglish?'new':'新词'}} · {{ goal.review_limit }} {{isEnglish?'review':'复习'}} · {{ goal.dictation }} {{isEnglish?'dictation':'听写'}}</small></span></div>
    </section>

    <p v-if="error" class="form-error welcome-error">{{ error }}</p>
    <footer class="welcome-actions"><button class="secondary-button" :disabled="step === 0" @click="back"><ChevronLeft />{{l.back}}</button><button v-if="step < 3" class="primary-button" :disabled="step === 1 && (!chapterId || !unitId)" @click="next">{{l.continue}}<ChevronRight /></button><button v-else class="primary-button" :disabled="busy || !unitId" @click="complete">{{ busy ? l.saving : l.enter }}<Sparkles /></button></footer>
  </main>
</template>

<style scoped>
/* ============================================================
   Stage 3 · 视图改造（门户组）：落地欢迎页奶玻璃化
   仅覆盖材料/颜色令牌，布局与排版保持原舒展密度；
   覆盖 styles.css 旧浅底规则（scoped 选择器优先级更高），
   凡覆盖基础态的选择器都同步重声明 active/checked 态防 tie。
   ============================================================ */

/* 主卡片：中玻璃（页面在 App 壳外，靠自带 ambient-bg 光斑透出质感） */
.welcome-card {
  background: var(--glass-bg);
  -webkit-backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-highlight), var(--glass-shadow);
}

/* 进度段：sage 软色令牌（暗色自动翻转），激活态保持 sage */
.welcome-progress span { background: var(--sage-soft); }
.welcome-progress span.active { background: var(--sage); }

/* 级别 Tab：免 blur 内嵌玻璃；激活态 accent 上文字用 on-accent（替换写死 #fff） */
.level-tabs button { min-height: 44px; border-color: var(--glass-border); background: var(--glass-bg-inset); }
.level-tabs button.active { border-color: var(--sage); background: var(--sage); color: var(--on-accent); }

/* 章节选择格：滚动网格，遵守免 blur 纪律用内嵌玻璃 */
.chapter-choice-grid button { border-color: var(--glass-border); background: var(--glass-bg-inset); }
.chapter-choice-grid button.active { border-color: var(--sage); background: var(--sage-soft); }

.unit-choice{margin-top:16px;padding-top:14px;border-top:1px solid var(--line)}
.unit-choice>p{margin:0 0 10px;color:var(--muted);font-size:13px}.unit-choice>div{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.unit-choice button{display:grid;gap:3px;min-height:58px;padding:10px 12px;border:1px solid var(--glass-border);border-radius:var(--radius-md);background:var(--glass-bg-inset);text-align:left;color:var(--ink)}
.unit-choice button.active{border-color:var(--sage);background:var(--sage-soft)}.unit-choice small{color:var(--muted);font-size:11px}

/* 偏好设置行：内嵌玻璃 */
.preference-list > label, .preference-list > div { border-color: var(--glass-border); background: var(--glass-bg-inset); }

/* 开关：轨道/滑块令牌化（替换写死 #d9ddd5 / #fff） */
.preference-list input[type=checkbox] { background: var(--muted-2); }
.preference-list input[type=checkbox]:after { background: var(--paper); }
.preference-list input[type=checkbox]:checked { background: var(--sage); }

/* 下拉：软玻璃 input 规范（替换写死 #fff） */
.preference-list select {
  border-color: var(--glass-border);
  background: var(--glass-bg-soft);
  -webkit-backdrop-filter: blur(var(--glass-blur-soft)) saturate(150%);
  backdrop-filter: blur(var(--glass-blur-soft)) saturate(150%);
  color: var(--ink);
}

/* 摘要条：金色调令牌化（与全局 backlog/smart-suggestion 暗色写法同族） */
.welcome-summary { background: rgba(210,169,78,.15); color: #8a6a22; }
[data-theme="dark"] .welcome-summary { background: rgba(210,169,78,.14); color: #dcc089; }

/* 降级：不支持 backdrop-filter 时回退近实色 */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .welcome-card, .preference-list select { background: var(--glass-fallback); -webkit-backdrop-filter: none; backdrop-filter: none; }
}
</style>
